import { useState, useRef, useCallback, useEffect } from 'react';
import { io, Socket } from 'socket.io-client';
import { getServerUrl } from '../../lib/getServerUrl';

export interface SoundboardButton {
  id: string;
  label: string;
  audioUrl: string;
}

export interface SoundboardGroup {
  id: string;
  label: string;
  collapsed: boolean;
  buttons: SoundboardButton[];
}

async function fetchGroups(): Promise<SoundboardGroup[]> {
  try {
    const res = await fetch('/api/soundboard/config');
    if (!res.ok) return [];
    const data = await res.json();
    return data.groups ?? [];
  } catch {
    return [];
  }
}

async function persistGroups(groups: SoundboardGroup[]) {
  try {
    await fetch('/api/soundboard/config', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ groups }),
    });
  } catch (err) {
    console.error('[Soundboard] Error saving config:', err);
  }
}

/** Cerca un button in tutti i gruppi */
function findButton(groups: SoundboardGroup[], buttonId: string): SoundboardButton | undefined {
  for (const g of groups) {
    const btn = g.buttons.find((b) => b.id === buttonId);
    if (btn) return btn;
  }
  return undefined;
}

function findButtonByLabel(groups: SoundboardGroup[], label: string): SoundboardButton | undefined {
  const lower = label.toLowerCase();
  for (const g of groups) {
    const btn = g.buttons.find((b) => b.label.toLowerCase() === lower);
    if (btn) return btn;
  }
  return undefined;
}

interface UseSoundboardOptions {
  /** If true, this instance will respond to server-triggered auto-play events (default: true) */
  autoPlayEnabled?: boolean;
}

export function useSoundboard({ autoPlayEnabled = true }: UseSoundboardOptions = {}) {
  const [groups, setGroups] = useState<SoundboardGroup[]>([]);
  const [activeButtonId, setActiveButtonId] = useState<string | null>(null);
  const [volume, setVolumeState] = useState(0.5);
  const [isEditMode, setIsEditMode] = useState(false);
  const [loop, setLoop] = useState(false);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const fadeTimeoutRef = useRef<number | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const ignoreRemoteRef = useRef(false);
  const autoPlayRef = useRef<((label: string) => void) | null>(null);
  const groupsRef = useRef<SoundboardGroup[]>([]);
  const volumeRef = useRef(0.5);

  // Load groups from server on mount
  useEffect(() => {
    fetchGroups().then(setGroups);
  }, []);

  // Keep refs in sync
  useEffect(() => { groupsRef.current = groups; }, [groups]);
  useEffect(() => { volumeRef.current = volume; }, [volume]);

  // Connect to /display namespace for soundboard coordination
  useEffect(() => {
    const serverUrl = getServerUrl('display');
    const sock = io(serverUrl, {
      transports: ['websocket', 'polling'],
    });
    socketRef.current = sock;

    sock.on('connect', () => {
      console.log('[Soundboard] Coordination socket connected');
    });

    sock.on('soundboard:remote-play', () => {
      if (ignoreRemoteRef.current) return;
      const audio = audioRef.current;
      if (audio && !audio.paused) {
        audio.pause();
        audio.currentTime = 0;
        const ctx = audioCtxRef.current;
        const gain = gainNodeRef.current;
        if (ctx && gain) {
          gain.gain.cancelScheduledValues(ctx.currentTime);
          gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
        }
      }
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
        fadeTimeoutRef.current = null;
      }
      setActiveButtonId(null);
    });

    if (autoPlayEnabled) {
      sock.on('soundboard:auto-play', (data: { label: string; delay?: number }) => {
        const delay = data.delay ?? 0;
        if (delay > 0) {
          setTimeout(() => autoPlayRef.current?.(data.label), delay);
        } else {
          autoPlayRef.current?.(data.label);
        }
      });

      // Effect sounds play on a separate Audio element so they don't interrupt main audio
      sock.on('soundboard:effect-play', (data: { label: string; delay?: number }) => {
        const fire = () => {
          const groups = groupsRef.current;
          const button = findButtonByLabel(groups, data.label);
          if (!button) {
            console.warn(`[Soundboard] Effect-play: suono "${data.label}" non trovato`);
            return;
          }
          console.log(`[Soundboard] Effect-play (overlay): "${data.label}"`);
          const sfx = new Audio(button.audioUrl);
          sfx.volume = Math.min(volumeRef.current * 1.5, 1);
          sfx.play().catch((err) => console.error('[Soundboard] Effect-play error:', err));
          sfx.addEventListener('ended', () => { sfx.src = ''; });
        };
        const delay = data.delay ?? 0;
        if (delay > 0) {
          setTimeout(fire, delay);
        } else {
          fire();
        }
      });
    }

    return () => {
      sock.disconnect();
      socketRef.current = null;
    };
  }, []);

  // Lazily init Web Audio API
  const ensureAudioContext = useCallback(() => {
    if (!audioRef.current) {
      audioRef.current = new Audio();
      audioRef.current.loop = loop;
      audioRef.current.addEventListener('ended', () => {
        setActiveButtonId(null);
      });
    } else {
      audioRef.current.loop = loop;
    }

    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
      gainNodeRef.current = audioCtxRef.current.createGain();
      sourceNodeRef.current = audioCtxRef.current.createMediaElementSource(audioRef.current);
      sourceNodeRef.current.connect(gainNodeRef.current);
      gainNodeRef.current.connect(audioCtxRef.current.destination);
    }

    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
  }, [loop]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.loop = loop;
    }
  }, [loop]);

  const stop = useCallback(() => {
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }

    const audio = audioRef.current;
    const ctx = audioCtxRef.current;
    const gain = gainNodeRef.current;

    if (!audio || !ctx || !gain) {
      setActiveButtonId(null);
      return;
    }

    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setValueAtTime(gain.gain.value, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.5);

    fadeTimeoutRef.current = window.setTimeout(() => {
      audio.pause();
      audio.currentTime = 0;
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(volume, ctx.currentTime);
      setActiveButtonId(null);
      fadeTimeoutRef.current = null;
    }, 500);

    socketRef.current?.emit('soundboard:stop');
  }, [volume]);

  const play = useCallback((id: string) => {
    ensureAudioContext();

    if (activeButtonId === id) {
      stop();
      return;
    }

    const button = findButton(groups, id);
    if (!button) return;

    const audio = audioRef.current!;
    const ctx = audioCtxRef.current!;
    const gain = gainNodeRef.current!;

    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
      fadeTimeoutRef.current = null;
    }

    audio.pause();
    audio.currentTime = 0;

    gain.gain.cancelScheduledValues(ctx.currentTime);
    gain.gain.setValueAtTime(volume, ctx.currentTime);

    ignoreRemoteRef.current = true;
    socketRef.current?.emit('soundboard:play');
    setTimeout(() => { ignoreRemoteRef.current = false; }, 100);

    audio.src = button.audioUrl;
    audio.play().catch((err) => console.error('[Soundboard] Play error:', err));
    setActiveButtonId(id);
  }, [activeButtonId, groups, ensureAudioContext, stop, volume]);

  const autoPlay = useCallback((label: string) => {
    const button = findButtonByLabel(groups, label);
    if (!button) {
      console.warn(`[Soundboard] Auto-play: suono "${label}" non trovato in playlist`);
      return;
    }
    console.log(`[Soundboard] Auto-play: "${label}"`);
    setLoop(false);
    play(button.id);
    if (audioRef.current) {
      audioRef.current.loop = false;
    }
  }, [groups, play, setLoop]);

  useEffect(() => {
    autoPlayRef.current = autoPlay;
  }, [autoPlay]);

  const setVolume = useCallback((v: number) => {
    setVolumeState(v);
    const ctx = audioCtxRef.current;
    const gain = gainNodeRef.current;
    if (ctx && gain) {
      gain.gain.cancelScheduledValues(ctx.currentTime);
      gain.gain.setValueAtTime(v, ctx.currentTime);
    }
  }, []);

  // --- Operazioni sui gruppi ---

  const addGroup = useCallback((label: string) => {
    setGroups((prev) => {
      const next = [...prev, {
        id: crypto.randomUUID(),
        label,
        collapsed: false,
        buttons: [],
      }];
      persistGroups(next);
      return next;
    });
  }, []);

  const removeGroup = useCallback((groupId: string) => {
    setGroups((prev) => {
      const next = prev.filter((g) => g.id !== groupId);
      persistGroups(next);
      return next;
    });
  }, []);

  const renameGroup = useCallback((groupId: string, newLabel: string) => {
    setGroups((prev) => {
      const next = prev.map((g) => g.id === groupId ? { ...g, label: newLabel } : g);
      persistGroups(next);
      return next;
    });
  }, []);

  const reorderGroups = useCallback((fromIndex: number, toIndex: number) => {
    setGroups((prev) => {
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      persistGroups(next);
      return next;
    });
  }, []);

  const toggleGroupCollapse = useCallback((groupId: string) => {
    setGroups((prev) => {
      const next = prev.map((g) => g.id === groupId ? { ...g, collapsed: !g.collapsed } : g);
      persistGroups(next);
      return next;
    });
  }, []);

  // --- Operazioni sui suoni (con groupId) ---

  const addButton = useCallback((groupId: string, label: string, audioUrl: string) => {
    const newButton: SoundboardButton = {
      id: crypto.randomUUID(),
      label,
      audioUrl,
    };
    setGroups((prev) => {
      const next = prev.map((g) =>
        g.id === groupId ? { ...g, buttons: [...g.buttons, newButton] } : g
      );
      persistGroups(next);
      return next;
    });
  }, []);

  const removeButton = useCallback(async (groupId: string, buttonId: string) => {
    const group = groups.find((g) => g.id === groupId);
    const button = group?.buttons.find((b) => b.id === buttonId);
    if (!button) return;

    if (activeButtonId === buttonId) {
      const audio = audioRef.current;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      setActiveButtonId(null);
    }

    try {
      await fetch('/api/upload/audio', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: button.audioUrl }),
      });
    } catch (err) {
      console.error('[Soundboard] Error deleting audio file:', err);
    }

    setGroups((prev) => {
      const next = prev.map((g) =>
        g.id === groupId ? { ...g, buttons: g.buttons.filter((b) => b.id !== buttonId) } : g
      );
      persistGroups(next);
      return next;
    });
  }, [groups, activeButtonId]);

  const renameButton = useCallback((groupId: string, buttonId: string, newLabel: string) => {
    setGroups((prev) => {
      const next = prev.map((g) =>
        g.id === groupId
          ? { ...g, buttons: g.buttons.map((b) => b.id === buttonId ? { ...b, label: newLabel } : b) }
          : g
      );
      persistGroups(next);
      return next;
    });
  }, []);

  const reorderButtons = useCallback((groupId: string, fromIndex: number, toIndex: number) => {
    setGroups((prev) => {
      const next = prev.map((g) => {
        if (g.id !== groupId) return g;
        const btns = [...g.buttons];
        const [moved] = btns.splice(fromIndex, 1);
        btns.splice(toIndex, 0, moved);
        return { ...g, buttons: btns };
      });
      persistGroups(next);
      return next;
    });
  }, []);

  const moveButton = useCallback((fromGroupId: string, buttonId: string, toGroupId: string) => {
    if (fromGroupId === toGroupId) return;
    setGroups((prev) => {
      let movedButton: SoundboardButton | undefined;
      const next = prev.map((g) => {
        if (g.id === fromGroupId) {
          movedButton = g.buttons.find((b) => b.id === buttonId);
          return { ...g, buttons: g.buttons.filter((b) => b.id !== buttonId) };
        }
        return g;
      }).map((g) => {
        if (g.id === toGroupId && movedButton) {
          return { ...g, buttons: [...g.buttons, movedButton] };
        }
        return g;
      });
      persistGroups(next);
      return next;
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      audioRef.current?.pause();
      audioCtxRef.current?.close();
    };
  }, []);

  return {
    groups,
    activeButtonId,
    volume,
    loop,
    setLoop,
    isEditMode,
    setIsEditMode,
    play,
    stop,
    setVolume,
    addGroup,
    removeGroup,
    renameGroup,
    reorderGroups,
    toggleGroupCollapse,
    addButton,
    removeButton,
    renameButton,
    reorderButtons,
    moveButton,
  };
}
